import Head from "next/head";
import { useRouter } from "next/router";
import Link from "next/link";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { INLINES, BLOCKS } from "@contentful/rich-text-types";
import { ExtendedNextPage } from "types/pages";

import Post from "components/Post";
import Label from "components/Label";
import HummingNav from "pages/components/HummingNav";
import HummingFooter from "pages/components/HummingFooter";
import { getPostBySlug, Post as ApiPost } from "utils/content/blog";
import Header from "../components/Header";
import NotFound from "../components/NotFound";

import styles from "./styles.module.scss";

const loaderProp = ({ src }: { src: string }) => {
  return src;
};

const renderOptions = {
  renderNode: {
    // eslint-disable-next-line
    [INLINES.ENTRY_HYPERLINK]: (node: any) => {
      const { data, content } = node;
      const [{ value }] = content;
      const { target: entry } = data || {
        target: { fields: { slug: "" } }
      };
      const {
        fields: { slug }
      } = entry;
      return <Link href={`/blog/${slug})}`}>{value}</Link>;
    },
    [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
      const { data } = node;
      return (
        <div className={styles.imageContainer}>
          <Image
            loader={loaderProp}
            src={`https://${data.target.fields.file.url}`}
            height={data.target.fields.file.details.image.height}
            width={data.target.fields.file.details.image.width}
            style={{ height: "auto", maxWidth: "100%" }}
            alt={data.target.fields.description}
          />
        </div>
      );
    },
    [BLOCKS.TABLE]: (node: any, children: any) => (
      <div style={{ overflowX: "auto" }}>
        <table className={styles.tableStyles}>
          <tbody>{children}</tbody>
        </table>
      </div>
    )
  }
};

const Page: ExtendedNextPage = () => {
  const router = useRouter();
  const {
    query: { context: routerContext }
  } = router;
  const context: { title: string; href: string } | null = routerContext
    ? JSON.parse(routerContext as string)
    : null;
  const { slug } = router.query as { slug: string };
  const [post, setPost] = useState<ApiPost>();
  const [loaded, setLoaded] = useState(false);

  async function fetchPost(postSlug: string) {
    const data = await getPostBySlug(postSlug);
    if (data) {
      setPost(data);
    }
    setLoaded(true);
  }

  useEffect(() => {
    if (slug) {
      fetchPost(slug);
    }
  }, [slug]);

  return !loaded ? null : post ? (
    <div className={styles.container}>
      <Head>
        <title>{post.fields.title} | Humming Homes</title>
        <meta
          name="description"
          content="We're industry experts, with a focus on ensuring any homeowner has the perfect set of information related to home services. From guides, to expert advice to comparisons, we've got you covered."
        />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      {post && (
        <>
          <Header
            title={post.fields.title}
            image={post.fields.thumbnail.fields.file.url}
            author={post.fields.author?.fields.name}
            date={post.sys.createdAt}
          />

          <div className={styles.postContainer}>
            <nav className={styles.breadcrumbs}>
              <div className={styles.breadcrumb}>
                <Link href="/blog">
                  <Label
                    variant="s-capitalized"
                    className={styles.breadcrumbLabel}
                  >
                    Blog
                  </Label>
                </Link>
              </div>
              {context && (
                <div className={styles.breadcrumb}>
                  <Link href={context.href}>
                    <Label
                      variant="s-capitalized"
                      className={styles.breadcrumbLabel}
                    >
                      {context.title}
                    </Label>
                  </Link>
                </div>
              )}
            </nav>

            <Post>
              <>{documentToReactComponents(post.fields.body, renderOptions)}</>
            </Post>
          </div>
        </>
      )}
    </div>
  ) : (
    <NotFound />
  );
};

Page.getLayout = (page) => {
  return (
    <div>
      <HummingNav />
      {page}
      <HummingFooter />
    </div>
  );
};

export default Page;
